import { Fragment, useEffect, useRef, useState } from "react";
import { api, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  FileDiff, Upload, X, Trash2, Wand2, Download, ArrowRight, ArrowLeft,
  Loader2, FileSpreadsheet, ShieldAlert, PlusCircle, History, ListChecks,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO, formatTanggal } from "@/lib/tanggal";

// Multi Rekon — cocokkan DUA berkas apa pun (Excel/CSV) baris demi baris.
// Kontrak API: lihat backend/routers/multirekon.py. Pencocokan baris murni
// deterministik di server (dict index by kunci) — halaman ini hanya UI.

const EMBER_ORDER = ["cocok", "selisih", "hanya_acuan", "hanya_banding", "duplikat_acuan", "duplikat_banding"];
const EMBER_META = {
  cocok:            { label: "Cocok",               tone: "emerald" },
  selisih:          { label: "Selisih",              tone: "amber" },
  hanya_acuan:      { label: "Hanya di Acuan",       tone: "rose" },
  hanya_banding:    { label: "Hanya di Pembanding",  tone: "rose" },
  duplikat_acuan:   { label: "Duplikat Acuan",       tone: "violet" },
  duplikat_banding: { label: "Duplikat Pembanding",  tone: "violet" },
};
const TONE_CLASSES = {
  emerald: { tab: "bg-emerald-50 text-emerald-700 border-emerald-300", dot: "bg-emerald-500" },
  amber:   { tab: "bg-amber-50 text-amber-700 border-amber-300", dot: "bg-amber-500" },
  rose:    { tab: "bg-rose-50 text-rose-700 border-rose-300", dot: "bg-rose-500" },
  violet:  { tab: "bg-violet-50 text-violet-700 border-violet-300", dot: "bg-violet-500" },
};

function fileKosong() {
  return {
    file: null, uploading: false,
    fileId: null, namaBerkas: "", sheetTerpilih: null, daftarSheet: [],
    barisHeader: 1, kolom: [], contohBaris: [], totalBaris: 0,
  };
}

const fmtTanggalJam = (iso) => {
  if (!iso) return "";
  try {
    return formatTanggal(iso, { denganJam: true });
  } catch { return ""; }
};

/** Nilai apa pun dari berkas pengguna bisa berupa teks, angka, atau tanggal —
 *  jangan paksa format Rupiah ke sana (kolomnya belum tentu uang). Angka
 *  hanya diberi pemisah ribuan ala Indonesia supaya mudah dibaca. */
function renderNilai(v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-300">—</span>;
  if (typeof v === "number") return fmtNum(v);
  return String(v);
}

const normalizeColName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function cocokkanKolomOtomatis(kolomAcuan, kolomBanding) {
  const used = new Set();
  const pairs = [];
  const cobaCocokkan = (predicate) => {
    for (const a of kolomAcuan) {
      if (pairs.some(p => p.acuan === a)) continue;
      const na = normalizeColName(a);
      let found = null;
      for (const b of kolomBanding) {
        if (used.has(b)) continue;
        if (predicate(na, normalizeColName(b))) { found = b; break; }
      }
      if (found) { pairs.push({ acuan: a, banding: found }); used.add(found); }
    }
  };
  cobaCocokkan((na, nb) => na.length > 0 && na === nb);
  cobaCocokkan((na, nb) => na.length >= 3 && nb.length >= 3 && (na.includes(nb) || nb.includes(na)));
  return pairs;
}

const KEY_LIKE_RE = /(kode|^id$|_id$|imei|sku|nomor|^no$|_no$|kunci)/i;
const terlihatSebagaiKunci = (nama) => KEY_LIKE_RE.test(String(nama || "").toLowerCase());

function tebakToleransi(namaKolom, contohBaris) {
  const lower = String(namaKolom || "").toLowerCase();
  if (/tanggal|tgl|date/.test(lower)) return { jenis: "tanggal_hari", nilai: 0 };
  const sampel = (contohBaris || []).map(r => r[namaKolom]).filter(v => v !== null && v !== undefined && v !== "");
  const semuaAngka = sampel.length > 0 && sampel.every(v => {
    if (typeof v === "number") return true;
    const bersih = String(v).trim().replace(/[.,]/g, "");
    return bersih !== "" && !isNaN(Number(bersih));
  });
  if (semuaAngka) return { jenis: "angka", nilai: 0 };
  return { jenis: "persis", nilai: 0 };
}

export default function MultiRekon() {
  const { can } = useAuth();
  const bolehLihat = can("multirekon", "view");
  const bolehBuat = can("multirekon", "create");
  const bolehHapus = can("multirekon", "delete");

  const [tab, setTab] = useState("proses"); // proses | riwayat
  const [step, setStep] = useState(1); // 1 unggah, 2 pencocokan, 3 hasil

  const [acuan, setAcuan] = useState(fileKosong());
  const [banding, setBanding] = useState(fileKosong());
  const uploadSeq = useRef({ acuan: 0, banding: 0 });

  const [kunci, setKunci] = useState([{ acuan: "", banding: "" }]);
  const [bandingKolom, setBandingKolom] = useState([]);
  const [opsi, setOpsi] = useState({ abaikan_besar_kecil: true, buang_spasi: true, buang_nol_depan: false });
  const [namaRun, setNamaRun] = useState("");
  const [menjalankan, setMenjalankan] = useState(false);

  const [hasil, setHasil] = useState(null); // { run_id, dibuat_pada, file_acuan_nama, file_banding_nama, ringkasan, nama_run }

  const unggahFile = (sisi, file, opts = {}) => {
    const setter = sisi === "acuan" ? setAcuan : setBanding;
    const seq = ++uploadSeq.current[sisi];
    setter(s => ({ ...s, uploading: true, file }));
    const fd = new FormData();
    fd.append("file", file);
    if (opts.sheet) fd.append("sheet", opts.sheet);
    if (opts.barisHeader) fd.append("baris_header", String(opts.barisHeader));
    api.post("/multirekon/unggah", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => {
        if (seq !== uploadSeq.current[sisi]) return;
        setter(s => ({
          ...s, uploading: false, file,
          fileId: data.file_id, namaBerkas: data.nama_berkas,
          sheetTerpilih: data.sheet_terpilih, daftarSheet: data.daftar_sheet || [],
          barisHeader: data.baris_header, kolom: data.kolom || [],
          contohBaris: data.contoh_baris || [], totalBaris: data.total_baris || 0,
        }));
      })
      .catch(e => {
        if (seq !== uploadSeq.current[sisi]) return;
        setter(s => ({ ...s, uploading: false }));
        toast.error(e?.response?.data?.detail || `Gagal mengunggah berkas ${sisi === "acuan" ? "Acuan" : "Pembanding"}.`);
      });
  };

  const pilihFile = (sisi, file) => unggahFile(sisi, file, {});
  const gantiSheet = (sisi, sheet) => unggahFile(sisi, (sisi === "acuan" ? acuan : banding).file, { sheet });
  const terapkanHeader = (sisi, n) => {
    const st = sisi === "acuan" ? acuan : banding;
    unggahFile(sisi, st.file, { sheet: st.sheetTerpilih, barisHeader: n });
  };
  const hapusFile = (sisi) => (sisi === "acuan" ? setAcuan : setBanding)(fileKosong());

  const jalankanRekon = async () => {
    const kunciValid = kunci.filter(k => k.acuan && k.banding);
    if (kunciValid.length === 0) {
      toast.error("Pilih minimal satu pasangan kolom kunci sebelum menjalankan rekonsiliasi.");
      return;
    }
    const bandingValid = bandingKolom.filter(b => b.acuan && b.banding);
    setMenjalankan(true);
    try {
      const namaRunTrim = namaRun.trim();
      const { data } = await api.post("/multirekon/jalankan", {
        file_acuan_id: acuan.fileId,
        file_banding_id: banding.fileId,
        kunci: kunciValid.map(k => ({ acuan: k.acuan, banding: k.banding })),
        banding_kolom: bandingValid.map(b => ({
          acuan: b.acuan, banding: b.banding,
          toleransi: { jenis: b.toleransiJenis || "persis", nilai: Number(b.toleransiNilai) || 0 },
        })),
        opsi,
        nama_run: namaRunTrim || undefined,
      });
      setHasil({
        run_id: data.run_id, dibuat_pada: data.dibuat_pada,
        file_acuan_nama: data.file_acuan_nama, file_banding_nama: data.file_banding_nama,
        ringkasan: data.ringkasan, nama_run: namaRunTrim || null,
      });
      setStep(3);
      toast.success("Rekonsiliasi selesai dijalankan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjalankan rekonsiliasi. Periksa kembali pemilihan kolom.");
    } finally {
      setMenjalankan(false);
    }
  };

  const mulaiBaru = () => {
    setAcuan(fileKosong());
    setBanding(fileKosong());
    setKunci([{ acuan: "", banding: "" }]);
    setBandingKolom([]);
    setOpsi({ abaikan_besar_kecil: true, buang_spasi: true, buang_nol_depan: false });
    setNamaRun("");
    setHasil(null);
    setStep(1);
  };

  const bukaDariRiwayat = (item) => {
    setHasil({
      run_id: item.id, dibuat_pada: item.dibuat_pada,
      file_acuan_nama: item.file_acuan_nama, file_banding_nama: item.file_banding_nama,
      ringkasan: item.ringkasan, nama_run: item.nama_run || null,
    });
    setStep(3);
    setTab("proses");
  };

  const bisaKembaliPencocokan = Boolean(acuan.fileId && banding.fileId);

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="multirekon-page">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Admin</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
            <FileDiff className="w-6 h-6 text-emerald-600" /> Multi Rekon
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Cocokkan dua berkas apa pun (Excel/CSV) baris demi baris — unggah berkas acuan (sumber kebenaran) dan berkas pembanding, lalu tentukan kolom kunci dan kolom yang dibandingkan.
          </p>
        </div>
      </div>

      {!bolehLihat ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="mr-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Multi Rekon</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin melihat modul ini.</p>
        </div>
      ) : (
        <>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1 w-fit">
            <button onClick={() => setTab("proses")} data-testid="mr-tab-proses"
                    className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "proses" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <ListChecks className="w-4 h-4" /> Rekonsiliasi
            </button>
            <button onClick={() => setTab("riwayat")} data-testid="mr-riwayat"
                    className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "riwayat" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <History className="w-4 h-4" /> Riwayat
            </button>
          </div>

          {tab === "riwayat" ? (
            <RiwayatView onBuka={bukaDariRiwayat} />
          ) : step === 3 && hasil ? (
            <HasilRekon
              hasil={hasil}
              bolehHapus={bolehHapus}
              bisaKembaliPencocokan={bisaKembaliPencocokan}
              onKembaliPencocokan={() => setStep(2)}
              onMulaiBaru={mulaiBaru}
              onHapusSukses={mulaiBaru}
            />
          ) : !bolehBuat ? (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="mr-tidak-boleh-buat">
              <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-700 font-medium">Anda hanya bisa melihat hasil rekonsiliasi</p>
              <p className="text-sm text-slate-500 mt-1">Izin "create" pada modul Multi Rekon diperlukan untuk mengunggah berkas dan menjalankan rekonsiliasi baru. Buka tab Riwayat untuk melihat hasil yang sudah ada.</p>
            </div>
          ) : (
            <>
              <StepBreadcrumb step={step} />
              {step === 1 ? (
                <LangkahUnggah
                  acuan={acuan} banding={banding}
                  onPilihFile={pilihFile} onGantiSheet={gantiSheet}
                  onTerapkanHeader={terapkanHeader} onHapusFile={hapusFile}
                  onLanjut={() => setStep(2)}
                />
              ) : (
                <LangkahPencocokan
                  acuan={acuan} banding={banding}
                  kunci={kunci} setKunci={setKunci}
                  bandingKolom={bandingKolom} setBandingKolom={setBandingKolom}
                  opsi={opsi} setOpsi={setOpsi}
                  namaRun={namaRun} setNamaRun={setNamaRun}
                  menjalankan={menjalankan}
                  onJalankan={jalankanRekon}
                  onKembali={() => setStep(1)}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function StepBreadcrumb({ step }) {
  const langkah = [
    { n: 1, label: "Unggah Berkas" },
    { n: 2, label: "Tentukan Pencocokan" },
    { n: 3, label: "Hasil" },
  ];
  return (
    <div className="flex items-center gap-2 text-xs">
      {langkah.map((l, i) => (
        <Fragment key={l.n}>
          {i > 0 && <span className="text-slate-300">›</span>}
          <span className={step === l.n ? "font-semibold text-emerald-700" : "text-slate-500"}>{l.n}. {l.label}</span>
        </Fragment>
      ))}
    </div>
  );
}

function Kartu({ label, nilai, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-xl font-bold mt-1.5 ${tone || "text-slate-900"}`}>{nilai}</p>
    </div>
  );
}

// ============ Langkah 1: Unggah ============
function LangkahUnggah({ acuan, banding, onPilihFile, onGantiSheet, onTerapkanHeader, onHapusFile, onLanjut }) {
  const siap = Boolean(acuan.fileId && banding.fileId && !acuan.uploading && !banding.uploading);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <KotakUnggah label="Berkas Acuan (sumber kebenaran)" sisi="acuan" state={acuan} testId="mr-file-acuan"
                     onPilihFile={f => onPilihFile("acuan", f)}
                     onGantiSheet={s => onGantiSheet("acuan", s)}
                     onTerapkanHeader={n => onTerapkanHeader("acuan", n)}
                     onHapus={() => onHapusFile("acuan")} />
        <KotakUnggah label="Berkas Pembanding" sisi="banding" state={banding} testId="mr-file-banding"
                     onPilihFile={f => onPilihFile("banding", f)}
                     onGantiSheet={s => onGantiSheet("banding", s)}
                     onTerapkanHeader={n => onTerapkanHeader("banding", n)}
                     onHapus={() => onHapusFile("banding")} />
      </div>
      <div className="flex justify-end">
        <button onClick={onLanjut} disabled={!siap} data-testid="mr-lanjut-pencocokan"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          Lanjut ke Pencocokan Kolom <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function KotakUnggah({ label, sisi, state, testId, onPilihFile, onGantiSheet, onTerapkanHeader, onHapus }) {
  const [barisHeaderDraft, setBarisHeaderDraft] = useState(state.barisHeader || 1);
  useEffect(() => { setBarisHeaderDraft(state.barisHeader || 1); }, [state.barisHeader]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-col gap-3" data-testid={`mr-kotak-${sisi}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        {state.fileId && (
          <button onClick={onHapus} data-testid={`mr-ganti-${sisi}`}
                  className="text-xs text-slate-400 hover:text-rose-600 inline-flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> Ganti Berkas
          </button>
        )}
      </div>

      {!state.fileId ? (
        <label className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30">
          <input type="file" accept=".xlsx,.csv" data-testid={testId} disabled={state.uploading}
                 className="hidden"
                 onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPilihFile(f); }} />
          {state.uploading ? (
            <span className="inline-flex flex-col items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" /> Mengunggah…
            </span>
          ) : (
            <span className="inline-flex flex-col items-center gap-2 text-slate-500 text-sm">
              <Upload className="w-6 h-6" />
              Klik untuk pilih berkas (.xlsx / .csv)
              <span className="text-xs text-slate-400">Maks 10 MB, 20.000 baris</span>
            </span>
          )}
        </label>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-medium text-slate-800 truncate">{state.namaBerkas}</span>
          </div>
          <p className="text-xs text-slate-500">{fmtNum(state.totalBaris)} baris · {state.kolom.length} kolom</p>

          {state.daftarSheet && state.daftarSheet.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Sheet</label>
              <select value={state.sheetTerpilih || ""} disabled={state.uploading}
                      onChange={e => onGantiSheet(e.target.value)}
                      data-testid={`mr-sheet-${sisi}`}
                      className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white">
                {state.daftarSheet.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 mb-1">Baris Header (mulai dari 1)</label>
              <input type="number" min={1} value={barisHeaderDraft} disabled={state.uploading}
                     onChange={e => setBarisHeaderDraft(e.target.value)}
                     data-testid={`mr-baris-header-${sisi}`}
                     className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <button onClick={() => onTerapkanHeader(Number(barisHeaderDraft) || 1)}
                    disabled={state.uploading || Number(barisHeaderDraft) === state.barisHeader}
                    data-testid={`mr-terapkan-header-${sisi}`}
                    className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-700 disabled:opacity-50">
              Terapkan
            </button>
          </div>

          {state.contohBaris.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">
                Pratinjau ({Math.min(state.contohBaris.length, 20)} dari {fmtNum(state.totalBaris)} baris)
              </p>
              <div className="border border-slate-100 rounded-lg overflow-x-auto max-h-48">
                <table className="text-xs w-max">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{state.kolom.map(k => <th key={k} className="px-2 py-1.5 text-left font-semibold text-slate-600 whitespace-nowrap">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {state.contohBaris.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {state.kolom.map(k => <td key={k} className="px-2 py-1 whitespace-nowrap text-slate-700">{renderNilai(r[k])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============ Langkah 2: Pencocokan ============
function LangkahPencocokan({
  acuan, banding, kunci, setKunci, bandingKolom, setBandingKolom,
  opsi, setOpsi, namaRun, setNamaRun, menjalankan, onJalankan, onKembali,
}) {
  const cocokkanOtomatis = () => {
    const pairs = cocokkanKolomOtomatis(acuan.kolom, banding.kolom);
    if (pairs.length === 0) {
      toast.error("Tidak ditemukan kolom dengan nama yang sama/mirip di kedua berkas — pasangkan kolom secara manual.");
      return;
    }
    const kunciKandidat = pairs.filter(p => terlihatSebagaiKunci(p.acuan) || terlihatSebagaiKunci(p.banding));
    const kunciBaru = kunciKandidat.length > 0 ? kunciKandidat : [pairs[0]];
    const sisaKolom = pairs.filter(p => !kunciBaru.includes(p));
    setKunci(kunciBaru.map(p => ({ acuan: p.acuan, banding: p.banding })));
    setBandingKolom(sisaKolom.map(p => {
      const t = tebakToleransi(p.acuan, acuan.contohBaris);
      return { acuan: p.acuan, banding: p.banding, toleransiJenis: t.jenis, toleransiNilai: t.nilai };
    }));
    toast.success(`Berhasil memasangkan ${pairs.length} kolom secara otomatis — silakan periksa dan sesuaikan.`);
  };

  const ubahKunci = (idx, row) => setKunci(k => k.map((r, i) => i === idx ? row : r));
  const hapusKunci = (idx) => setKunci(k => k.filter((_, i) => i !== idx));
  const tambahKunci = () => setKunci(k => [...k, { acuan: "", banding: "" }]);

  const ubahBanding = (idx, row) => setBandingKolom(b => b.map((r, i) => i === idx ? row : r));
  const hapusBanding = (idx) => setBandingKolom(b => b.filter((_, i) => i !== idx));
  const tambahBanding = () => setBandingKolom(b => [...b, { acuan: "", banding: "", toleransiJenis: "persis", toleransiNilai: 0 }]);

  const kunciSiap = kunci.some(k => k.acuan && k.banding);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          <span className="font-medium text-slate-800">{acuan.namaBerkas}</span> ({fmtNum(acuan.totalBaris)} baris)
          <span className="text-slate-400 mx-2">vs</span>
          <span className="font-medium text-slate-800">{banding.namaBerkas}</span> ({fmtNum(banding.totalBaris)} baris)
        </p>
        <button onClick={cocokkanOtomatis} data-testid="mr-cocokkan-otomatis"
                className="h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold inline-flex items-center gap-1.5">
          <Wand2 className="w-3.5 h-3.5" /> Cocokkan Otomatis
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">Kolom Kunci</p>
            <p className="text-xs text-slate-500 mt-0.5">Kolom yang dipakai untuk mencocokkan baris antar berkas. Pilih lebih dari satu kalau kuncinya gabungan (mis. Tanggal + Kode SKU).</p>
          </div>
          <button onClick={tambahKunci} data-testid="mr-kunci-tambah"
                  className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 inline-flex items-center gap-1.5 shrink-0">
            <PlusCircle className="w-3.5 h-3.5" /> Tambah Kunci
          </button>
        </div>
        <div className="space-y-2">
          {kunci.map((row, idx) => (
            <BarisKunci key={idx} idx={idx} row={row} kolomAcuan={acuan.kolom} kolomBanding={banding.kolom}
                        onChange={r => ubahKunci(idx, r)} onHapus={() => hapusKunci(idx)} bolehHapus={kunci.length > 1} />
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">Kolom Dibandingkan</p>
            <p className="text-xs text-slate-500 mt-0.5">Kolom yang nilainya dibandingkan antar berkas untuk baris yang kuncinya cocok, beserta toleransinya.</p>
          </div>
          <button onClick={tambahBanding} data-testid="mr-banding-tambah"
                  className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 inline-flex items-center gap-1.5 shrink-0">
            <PlusCircle className="w-3.5 h-3.5" /> Tambah Kolom
          </button>
        </div>
        {bandingKolom.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Belum ada kolom dibandingkan — baris dengan kunci yang sama otomatis dianggap "Cocok".</p>
        ) : (
          <div className="space-y-2">
            {bandingKolom.map((row, idx) => (
              <BarisBanding key={idx} idx={idx} row={row} kolomAcuan={acuan.kolom} kolomBanding={banding.kolom}
                            onChange={r => ubahBanding(idx, r)} onHapus={() => hapusBanding(idx)} />
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-2">
        <p className="text-sm font-semibold text-slate-800 mb-1">Opsi Pencocokan</p>
        <OpsiToggle testId="mr-opsi-besar-kecil" checked={opsi.abaikan_besar_kecil}
                    onChange={v => setOpsi(o => ({ ...o, abaikan_besar_kecil: v }))}
                    judul="Abaikan besar-kecil huruf"
                    keterangan={'"ABC123" dianggap sama dengan "abc123".'} />
        <OpsiToggle testId="mr-opsi-buang-spasi" checked={opsi.buang_spasi}
                    onChange={v => setOpsi(o => ({ ...o, buang_spasi: v }))}
                    judul="Buang spasi"
                    keterangan={'Spasi di awal/akhir dibuang sebelum dibandingkan, supaya " ABC123 " cocok dengan "ABC123".'} />
        <OpsiToggle testId="mr-opsi-buang-nol-depan" checked={opsi.buang_nol_depan}
                    onChange={v => setOpsi(o => ({ ...o, buang_nol_depan: v }))}
                    judul="Buang nol di depan"
                    keterangan={'Supaya IMEI 0123456789012 cocok dengan 123456789012 (nol depan diabaikan).'} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <label className="block text-xs font-medium text-slate-600 mb-1">Nama Rekonsiliasi (opsional)</label>
        <input type="text" value={namaRun} onChange={e => setNamaRun(e.target.value)}
               placeholder="Contoh: Rekon Stok Gudang Agustus 2026" data-testid="mr-nama-run"
               className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onKembali} disabled={menjalankan} data-testid="mr-kembali-unggah"
                className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>
        <button onClick={onJalankan} disabled={menjalankan || !kunciSiap} data-testid="mr-jalankan"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {menjalankan ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDiff className="w-4 h-4" />}
          {menjalankan ? "Menjalankan…" : "Jalankan Rekonsiliasi"}
        </button>
      </div>
    </div>
  );
}

function OpsiToggle({ checked, onChange, judul, keterangan, testId }) {
  return (
    <label className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-100 cursor-pointer select-none hover:bg-slate-50">
      <input type="checkbox" checked={checked} data-testid={testId}
             onChange={e => onChange(e.target.checked)}
             className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-emerald-600" />
      <span>
        <span className="block text-sm font-medium text-slate-800">{judul}</span>
        <span className="block text-xs text-slate-500">{keterangan}</span>
      </span>
    </label>
  );
}

function BarisKunci({ idx, row, kolomAcuan, kolomBanding, onChange, onHapus, bolehHapus }) {
  return (
    <div className="flex items-center gap-2" data-testid={`mr-kunci-baris-${idx}`}>
      <select value={row.acuan} onChange={e => onChange({ ...row, acuan: e.target.value })}
              data-testid={`mr-kunci-acuan-${idx}`}
              className="flex-1 h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white">
        <option value="">Pilih kolom acuan…</option>
        {kolomAcuan.map(k => <option key={k} value={k}>{k}</option>)}
      </select>
      <span className="text-slate-400 text-xs shrink-0">↔</span>
      <select value={row.banding} onChange={e => onChange({ ...row, banding: e.target.value })}
              data-testid={`mr-kunci-banding-${idx}`}
              className="flex-1 h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white">
        <option value="">Pilih kolom pembanding…</option>
        {kolomBanding.map(k => <option key={k} value={k}>{k}</option>)}
      </select>
      {bolehHapus && (
        <button onClick={onHapus} data-testid={`mr-kunci-hapus-${idx}`}
                className="text-slate-400 hover:text-rose-600 shrink-0" title="Hapus baris kunci">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function BarisBanding({ idx, row, kolomAcuan, kolomBanding, onChange, onHapus }) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-slate-100" data-testid={`mr-banding-baris-${idx}`}>
      <select value={row.acuan} onChange={e => onChange({ ...row, acuan: e.target.value })}
              data-testid={`mr-banding-acuan-${idx}`}
              className="h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[10rem] flex-1">
        <option value="">Kolom acuan…</option>
        {kolomAcuan.map(k => <option key={k} value={k}>{k}</option>)}
      </select>
      <span className="text-slate-400 text-xs shrink-0">vs</span>
      <select value={row.banding} onChange={e => onChange({ ...row, banding: e.target.value })}
              data-testid={`mr-banding-banding-${idx}`}
              className="h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[10rem] flex-1">
        <option value="">Kolom pembanding…</option>
        {kolomBanding.map(k => <option key={k} value={k}>{k}</option>)}
      </select>
      <select value={row.toleransiJenis} onChange={e => onChange({ ...row, toleransiJenis: e.target.value })}
              data-testid={`mr-banding-toleransi-${idx}`}
              className="h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white shrink-0">
        <option value="persis">Persis sama</option>
        <option value="angka">Angka ± selisih</option>
        <option value="persen">Persen (%) selisih</option>
        <option value="tanggal_hari">Tanggal ± hari</option>
      </select>
      {row.toleransiJenis !== "persis" && (
        <input type="number" value={row.toleransiNilai} onChange={e => onChange({ ...row, toleransiNilai: e.target.value })}
               placeholder={row.toleransiJenis === "tanggal_hari" ? "hari" : row.toleransiJenis === "persen" ? "%" : "±"}
               data-testid={`mr-banding-nilai-${idx}`}
               className="w-20 h-9 px-2 border border-slate-200 rounded-lg text-sm shrink-0" />
      )}
      <button onClick={onHapus} data-testid={`mr-banding-hapus-${idx}`}
              className="text-slate-400 hover:text-rose-600 ml-auto shrink-0" title="Hapus kolom banding">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============ Langkah 3: Hasil ============
function HasilRekon({ hasil, bolehHapus, bisaKembaliPencocokan, onKembaliPencocokan, onMulaiBaru, onHapusSukses }) {
  const [emberAktif, setEmberAktif] = useState("cocok");
  const [emberOffset, setEmberOffset] = useState(0);
  const emberLimit = 50;
  const [emberRows, setEmberRows] = useState([]);
  const [emberTotal, setEmberTotal] = useState(0);
  const [emberLoading, setEmberLoading] = useState(false);
  const [mengekspor, setMengekspor] = useState(false);
  const [menghapus, setMenghapus] = useState(false);
  const emberSeq = useRef(0);

  useEffect(() => { setEmberOffset(0); }, [emberAktif, hasil.run_id]);

  useEffect(() => {
    const seq = ++emberSeq.current;
    setEmberLoading(true);
    api.get(`/multirekon/hasil/${hasil.run_id}`, { params: { ember: emberAktif, offset: emberOffset, limit: emberLimit } })
      .then(r => {
        if (seq !== emberSeq.current) return;
        setEmberRows(r.data.baris || []);
        setEmberTotal(r.data.total_baris_ember || 0);
      })
      .catch(e => {
        if (seq !== emberSeq.current) return;
        toast.error(e?.response?.data?.detail || "Gagal memuat detail hasil rekonsiliasi");
      })
      .finally(() => { if (seq === emberSeq.current) setEmberLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasil.run_id, emberAktif, emberOffset]);

  const eksporHasil = async () => {
    setMengekspor(true);
    try {
      const res = await api.get(`/multirekon/hasil/${hasil.run_id}/export`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = hariIniISO();
      const label = (hasil.nama_run || `${hasil.file_acuan_nama}_vs_${hasil.file_banding_nama}`).replace(/[^\w-]+/g, "_");
      a.download = `Rekon_${label}_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Berkas hasil rekonsiliasi berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor hasil rekonsiliasi ke Excel");
    } finally {
      setMengekspor(false);
    }
  };

  const hapusHasil = async () => {
    if (!window.confirm("Hapus hasil rekonsiliasi ini? Tindakan ini tidak bisa dibatalkan.")) return;
    setMenghapus(true);
    try {
      await api.delete(`/multirekon/hasil/${hasil.run_id}`);
      toast.success("Hasil rekonsiliasi dihapus");
      onHapusSukses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus hasil rekonsiliasi");
    } finally {
      setMenghapus(false);
    }
  };

  const r = hasil.ringkasan;

  return (
    <div className="space-y-4" data-testid="mr-hasil">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Hasil Rekonsiliasi</p>
          <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">
            {hasil.file_acuan_nama} <span className="text-slate-400 font-normal">vs</span> {hasil.file_banding_nama}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {fmtTanggalJam(hasil.dibuat_pada)}{hasil.nama_run ? ` · ${hasil.nama_run}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bisaKembaliPencocokan && (
            <button onClick={onKembaliPencocokan} data-testid="mr-kembali-pencocokan"
                    className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
              Ubah Pencocokan
            </button>
          )}
          <button onClick={onMulaiBaru} data-testid="mr-mulai-baru"
                  className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
            Rekonsiliasi Baru
          </button>
          <button onClick={eksporHasil} disabled={mengekspor} data-testid="mr-export"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {mengekspor ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {mengekspor ? "Mengekspor…" : "Export Excel"}
          </button>
          {bolehHapus && (
            <button onClick={hapusHasil} disabled={menghapus} data-testid="mr-hapus"
                    className="h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              <Trash2 className="w-4 h-4" /> {menghapus ? "Menghapus…" : "Hapus"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kartu label="Baris Berkas Acuan" nilai={fmtNum(r.total_baris_acuan)} />
        <Kartu label="Baris Berkas Pembanding" nilai={fmtNum(r.total_baris_banding)} />
        <Kartu label="Persentase Cocok" nilai={`${r.persentase_cocok}%`}
               tone={r.persentase_cocok >= 95 ? "text-emerald-700" : r.persentase_cocok >= 80 ? "text-amber-700" : "text-rose-700"} />
        <Kartu label="Total Baris Selisih" nilai={fmtNum(r.jumlah.selisih)} tone="text-amber-700" />
      </div>

      {Object.keys(r.total_selisih_absolut || {}).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Total Selisih Absolut per Pasangan Kolom (angka)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(r.total_selisih_absolut).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-sm">
                <span className="text-slate-600 truncate">{k}</span>
                <span className="font-mono font-semibold text-amber-700">{fmtNum(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {EMBER_ORDER.map(k => {
          const tone = TONE_CLASSES[EMBER_META[k].tone];
          const aktif = emberAktif === k;
          return (
            <button key={k} onClick={() => setEmberAktif(k)} data-testid={`mr-tab-${k}`}
                    className={`px-3 h-9 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 ${aktif ? tone.tab : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
              {EMBER_META[k].label}
              <span className="opacity-70">({fmtNum(r.jumlah[k] || 0)})</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {emberLoading ? (
          <LoadingState loading data-testid="mr-ember-loading" />
        ) : (emberAktif === "cocok" || emberAktif === "selisih") ? (
          <TabelBandingKolom rows={emberRows} />
        ) : (
          <TabelSisiTunggal rows={emberRows} ember={emberAktif} />
        )}
        {emberTotal > emberLimit && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
            <button disabled={emberOffset <= 0} onClick={() => setEmberOffset(o => Math.max(0, o - emberLimit))}
                    data-testid="mr-hasil-prev" className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">Sebelumnya</button>
            <span className="text-slate-500">
              Baris {fmtNum(emberOffset + 1)}–{fmtNum(Math.min(emberOffset + emberLimit, emberTotal))} dari {fmtNum(emberTotal)}
            </span>
            <button disabled={emberOffset + emberLimit >= emberTotal} onClick={() => setEmberOffset(o => o + emberLimit)}
                    data-testid="mr-hasil-next" className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">Berikutnya</button>
          </div>
        )}
      </div>
    </div>
  );
}

function TabelBandingKolom({ rows }) {
  if (rows.length === 0) {
    return <p className="p-8 text-center text-sm text-slate-400">Tidak ada baris pada kategori ini.</p>;
  }
  const specs = (rows[0].kolom_banding || []).map(kb => ({ acuan: kb.kolom_acuan, banding: kb.kolom_banding }));
  if (specs.length === 0) {
    // Tidak ada kolom dibandingkan sama sekali — hanya tampilkan kunci.
    return (
      <div className="overflow-x-auto">
        <table className="text-sm w-full">
          <thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2 text-left font-semibold border-b border-slate-200">Kunci</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, ri) => (
              <tr key={row.id || ri}><td className="px-3 py-2 font-mono text-xs text-slate-600">{row.kunci}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="text-sm w-max min-w-full">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th rowSpan={2} className="px-3 py-2 text-left font-semibold border-b border-r border-slate-200 align-bottom">Kunci</th>
            {specs.map((sp, i) => (
              <th key={i} colSpan={3} className="px-3 py-1.5 text-center font-semibold border-b border-r border-slate-200 whitespace-nowrap">
                {sp.acuan} <span className="text-slate-400">vs</span> {sp.banding}
              </th>
            ))}
          </tr>
          <tr>
            {specs.map((sp, i) => (
              <Fragment key={i}>
                <th className="px-3 py-1.5 text-right font-medium border-b border-slate-200 text-[11px] text-slate-500">Acuan</th>
                <th className="px-3 py-1.5 text-right font-medium border-b border-slate-200 text-[11px] text-slate-500">Pembanding</th>
                <th className="px-3 py-1.5 text-right font-medium border-b border-r border-slate-200 text-[11px] text-slate-500">Delta</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, ri) => (
            <tr key={row.id || ri} className="hover:bg-slate-50/60">
              <td className="px-3 py-2 font-mono text-xs text-slate-600 border-r border-slate-100 whitespace-nowrap">{row.kunci}</td>
              {(row.kolom_banding || []).map((kb, ci) => (
                <Fragment key={ci}>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${!kb.cocok ? "bg-rose-50 text-rose-800 font-medium" : "text-slate-700"}`}>{renderNilai(kb.nilai_acuan)}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${!kb.cocok ? "bg-rose-50 text-rose-800 font-medium" : "text-slate-700"}`}>{renderNilai(kb.nilai_banding)}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap border-r border-slate-100 ${!kb.cocok ? "bg-rose-50 text-rose-800 font-semibold" : "text-slate-400"}`}>
                    {kb.delta === null || kb.delta === undefined ? "-" : renderNilai(kb.delta)}
                  </td>
                </Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabelSisiTunggal({ rows, ember }) {
  if (rows.length === 0) {
    return <p className="p-8 text-center text-sm text-slate-400">Tidak ada baris pada kategori ini.</p>;
  }
  const sisiKey = ember === "hanya_acuan" || ember === "duplikat_acuan" ? "acuan" : "banding";
  const isDuplikat = ember.startsWith("duplikat");
  const kolom = Object.keys(rows[0][sisiKey] || {});
  return (
    <div className="overflow-x-auto">
      <table className="text-sm w-max min-w-full">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left font-semibold border-b border-slate-200">Kunci</th>
            {kolom.map(k => <th key={k} className="px-3 py-2 text-left font-semibold border-b border-slate-200 whitespace-nowrap">{k}</th>)}
            <th className="px-3 py-2 text-left font-semibold border-b border-slate-200">{isDuplikat ? "Jumlah Duplikat" : "Catatan"}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, ri) => (
            <tr key={row.id || ri} className="hover:bg-slate-50/60">
              <td className="px-3 py-2 font-mono text-xs text-slate-600 whitespace-nowrap">{row.kunci}</td>
              {kolom.map(k => <td key={k} className="px-3 py-2 whitespace-nowrap text-slate-700">{renderNilai((row[sisiKey] || {})[k])}</td>)}
              <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{isDuplikat ? row.jumlah_duplikat : (row.catatan || "-")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============ Riwayat ============
function RiwayatView({ onBuka }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const muat = () => {
    const s = ++seq.current;
    setLoading(true);
    api.get("/multirekon/riwayat", { params: { limit, offset } })
      .then(r => {
        if (s !== seq.current) return;
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
      })
      .catch(e => { if (s === seq.current) toast.error(e?.response?.data?.detail || "Gagal memuat riwayat rekonsiliasi"); })
      .finally(() => { if (s === seq.current) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { muat(); }, [offset]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mr-riwayat-list">
      <LoadingState
        loading={loading}
        kosong={!loading && items.length === 0}
        pesanKosong="Belum ada riwayat rekonsiliasi."
        data-testid="mr-riwayat-loading"
      />
      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Tanggal</th>
                <th className="px-3 py-2 font-semibold">Nama</th>
                <th className="px-3 py-2 font-semibold">Berkas Acuan</th>
                <th className="px-3 py-2 font-semibold">Berkas Pembanding</th>
                <th className="px-3 py-2 font-semibold text-right">% Cocok</th>
                <th className="px-3 py-2 font-semibold text-right">Selisih</th>
                <th className="px-3 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtTanggalJam(item.dibuat_pada)}</td>
                  <td className="px-3 py-2 text-slate-700">{item.nama_run || <span className="text-slate-300 italic">tanpa nama</span>}</td>
                  <td className="px-3 py-2 text-slate-700 truncate max-w-[12rem]">{item.file_acuan_nama}</td>
                  <td className="px-3 py-2 text-slate-700 truncate max-w-[12rem]">{item.file_banding_nama}</td>
                  <td className="px-3 py-2 text-right font-medium text-slate-800">{item.ringkasan?.persentase_cocok ?? 0}%</td>
                  <td className="px-3 py-2 text-right text-amber-700">{fmtNum(item.ringkasan?.jumlah?.selisih || 0)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onBuka(item)} data-testid={`mr-riwayat-buka-${item.id}`}
                            className="text-xs font-medium text-emerald-700 hover:underline">Buka</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > limit && (
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
          <button disabled={offset <= 0} onClick={() => setOffset(o => Math.max(0, o - limit))}
                  data-testid="mr-riwayat-prev" className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">Sebelumnya</button>
          <span className="text-slate-500">{fmtNum(offset + 1)}–{fmtNum(Math.min(offset + limit, total))} dari {fmtNum(total)}</span>
          <button disabled={offset + limit >= total} onClick={() => setOffset(o => o + limit)}
                  data-testid="mr-riwayat-next" className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">Berikutnya</button>
        </div>
      )}
    </div>
  );
}
